import { RichText } from 'prismic-reactjs';
import { ISubscriptionForm } from './types';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import {
  format,
  formatDate,
  getEndOfFreeTrialDate,
} from '@monofront/business/utils';
import { useSessionStorage, useSubscription } from '@monofront/business/hooks';
import { useAuthentication } from '@context/businessContainer';
import { trackFreeTrialStarted } from '@utils/tracking';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { SESSIONSTORAGE_ENGAGED_FREETRIALER_CONFIRMATION } from '@constants/sessionStorage';
import { useTranslations } from 'next-intl';
import useLocalizedRoutes from '@hooks/i18n/useLocalizedRoutes';
import { RoutesEnum } from '@monofront/types';
import { useMembershipCouponContext } from '@context/membershipCoupon';
import useSubscriptionPrice from '@hooks/useSubscriptionPrice';
import withSlice from '@screens/SlicesContainer/withSlice';
import { mapSubscriptionForm } from './mapSubscriptionForm';
import CouponForm from '@components/CouponForm';
import CustomPrismicHeading from '@components/CustomPrismicHeading';
import Locker from '@monofront/design-system/visuals/Icons/Locker';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import SubscriptionPaymentMethodsList from '@components/SubscriptionPaymentMethodsList/SubscriptionPaymentMethodsList';

const SubscriptionForm: React.FC<ISubscriptionForm> = (contents) => {
  const [hasSubscriptionFormSucceeded, setHasSubscriptionFormSucceeded] =
    useState(false);
  const [isWaitingMemberStatus, setIsWaitingMemberStatus] = useState(false);
  const { getPath } = useLocalizedRoutes();
  const router = useRouter();
  const { validatedCoupon, discountedPrice, isCouponValid } =
    useMembershipCouponContext();

  const [, storeFreeTrialConfirmation] = useSessionStorage(
    SESSIONSTORAGE_ENGAGED_FREETRIALER_CONFIRMATION,
    '',
  );
  const { isMember, authUser } = useAuthentication();
  const { setPromotionCode } = useSubscription(
    !!isMember,
    authUser?.email,
    trackFreeTrialStarted,
  );
  const { shoppingCart } = useShoppingCart();
  const hasOnlyGiftItem = Boolean(
    shoppingCart.hasGiftItem && shoppingCart.products.length === 1,
  );
  const hasItemsInCart = shoppingCart.totalQuantity > 0 && !hasOnlyGiftItem;
  const { annualSubscriptionPrice } = useSubscriptionPrice();

  const tGlobal = useTranslations('Global');

  const promotionCode = validatedCoupon?.id || '';
  const promotionName = validatedCoupon?.code || '';

  useEffect(() => {
    if (promotionCode !== '') {
      setPromotionCode({ id: promotionCode, code: promotionName });
    }
  }, [promotionCode, setPromotionCode]);

  useEffect(() => {
    if (hasSubscriptionFormSucceeded && isMember) {
      const redirectPath = hasItemsInCart
        ? '/cart'
        : getPath(RoutesEnum.SUBSCRIPTION_CONFIRMATION);

      router.push(redirectPath);
    }
  }, [hasSubscriptionFormSucceeded, isMember]);

  const serializeRichTextHtmlPrice = (
    _type: string,
    _element: any,
    text: string,
  ) => {
    if (text?.includes('%PRICE%') || text?.includes('%DATE%')) {
      return (
        <>
          {isCouponValid && text?.includes('%PRICE%') && (
            <>
              <span className="line-through tw-lf-body-bold-md">
                {format(annualSubscriptionPrice)}
              </span>
              &nbsp;
            </>
          )}
          <span className="tw-lf-body-bold-md">
            {text
              ?.replace('%PRICE%', format(discountedPrice))
              .replace('%DATE%', '')}
          </span>
          <span className="tw-lf-body-bold-md">
            {text
              ?.replace('%PRICE%', '')
              .replace('%DATE%', formatDate(getEndOfFreeTrialDate()))}
          </span>
        </>
      );
    }

    return null;
  };

  return (
    <>
      <section className="tw-p-8">
        <div className="title tw-mb-2 tw-text-center">
          <RichText
            render={contents.selectPaymentMethodTitle}
            htmlSerializer={CustomPrismicHeading({
              className:
                'tw-lf-title tw-lf-title-sm tw-text-center desktop:tw-lf-title-md',
            })}
          />
        </div>
        {contents.isCouponFormDisplayed && (
          <CouponForm
            headings={
              <div className="tw-mb-4">
                <RichText
                  render={contents.couponFormHeadings}
                  htmlSerializer={serializeRichTextHtmlPrice}
                />
              </div>
            }
            openFormButtonText={contents.couponLinkButton}
            submitButtonText={contents.couponButton}
            onSuccessMessage={contents.couponSuccess}
            onErrorMessage={contents.couponError}
          />
        )}
        <SubscriptionPaymentMethodsList
          isLoading={isWaitingMemberStatus}
          ibanAboveButtonText={contents.ibanAboveButtonText}
          submitButtonLabel={contents.buttonLabel}
          subscribeCallback={(isNewSubscription) => {
            if (isNewSubscription) {
              setIsWaitingMemberStatus(true);
              storeFreeTrialConfirmation(
                JSON.stringify({
                  freeTrialStarted: true,
                  showModal: true,
                }),
              );
              setHasSubscriptionFormSucceeded(true);
            } else {
              toast.error(tGlobal('errorOccurredTryAgain'));
            }
          }}
        />
        {contents.securedPaymentWithLocker && (
          <div className="tw-flew tw-mt-2 tw-flex tw-justify-center">
            <span className="tw-lf-caption-regular tw-mr-1">
              {contents.securedPaymentWithLocker}
            </span>
            <Locker size={IconSizes.sm} />
          </div>
        )}
      </section>

      <style jsx>{`
        section {
          display: flex;
          flex-direction: column;
          justify-content: center;
          background-color: var(--white);
          border-radius: var(--rd-6);
          width: 600px;
          margin: auto;
        }

        section:global(strong) {
          font-weight: 600;
        }

        section :global(p) {
          line-height: 1.3em;
        }

        @media (max-width: 800px) {
          section {
            background: none;
            border-radius: 0;
            width: inherit;
            padding: 0;
          }
        }
      `}</style>
    </>
  );
};

export default withSlice(SubscriptionForm, mapSubscriptionForm);
